import { i18n } from '@lingui/core'
import { Rating } from '@mui/material'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import notRecommend from '../../assets/images/thumbs_down.svg'
import recommend from '../../assets/images/thumbs_up.svg'
import { formatTimeAgo } from '../../hooks/displayTools'
import useCallAPI from '../../hooks/useCallAPI'
import { AlertPrompt } from '../Prompt/AlertPrompt'
import { ErrorPrompt } from '../Prompt/ErrorPrompt'
import { Spinner } from '../Spinner'
import { ReviewItemImageList } from './ReviewItemImageList'

export type ReviewUserProps = {
  user_id: number
  display_name: string
  social_image: string
  user_type: string
  is_social_connected: number
  customer?: any | undefined
}

export type ReviewImageProps = {
  thumb_url: string
  original_url: string
}

export type ReviewItemProps = {
  id: number
  title?: string
  content: string
  score: number
  votes_up?: number
  votes_down?: number
  created_at: string
  sentiment?: number
  verified_buyer?
  user?: ReviewUserProps
  images_data: ReviewImageProps[]
  customer: any | undefined
  displayVotes?: boolean
  displayImages?: boolean
  displayAvatar?: boolean
  avatar_url?: string
  userName?: string | null | undefined
  displayDate?: boolean
  isHomePageItem?: boolean
  comment?: any | undefined
  displayReplies?: boolean
  companyName?: string | null | undefined
  yotpo_id?: number
}

export function ReviewItem(props: ReviewItemProps) {
  const {
    created_at,
    id,
    verified_buyer,
    user,
    images_data,
    title = '',
    content,
    votes_up,
    votes_down,
    sentiment,
    customer,
    displayVotes = false,
    displayImages = true,
    displayAvatar = false,
    displayReplies = true,
    avatar_url,
    userName,
    displayDate = true,
    isHomePageItem,
    comment,
    companyName,
    yotpo_id,
  } = props
  const lastUpdateDate = new Date(created_at)
  const numOfDaysPosted = Math.ceil(
    // eslint-disable-next-line no-unsafe-optional-chaining
    lastUpdateDate ? (Date.now() - lastUpdateDate?.getTime()) / 86400000 : 0,
  )

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const commentDate = new Date(comment?.created_at)
  const commentNumOfDaysPosted = Math.ceil(
    // eslint-disable-next-line no-unsafe-optional-chaining
    commentDate ? (Date.now() - commentDate?.getTime()) / 86400000 : 0,
  )

  const isUserLoggedIn = !!customer
  const {
    error,
    apiCall: voteUpApiCall,
    loading: voteUpLoading,
  } = useCallAPI({
    url: `${process.env.NEXT_PUBLIC_YOTPO_BASE_URL}/reviews/${id}/vote/up`,
    contentType: 'application/json',
    method: 'POST',
  })

  const {
    error: voteDownError,
    apiCall: voteDownApiCall,
    loading: voteDownLoading,
  } = useCallAPI({
    url: `${process.env.NEXT_PUBLIC_YOTPO_BASE_URL}/reviews/${id}/vote/down`,
    contentType: 'application/json',
    method: 'POST',
  })

  const {
    error: undoVoteUpApiCallError,
    apiCall: undoVoteUpApiCall,
    loading: undoVoteUpLoading,
  } = useCallAPI({
    url: `${process.env.NEXT_PUBLIC_YOTPO_BASE_URL}/reviews/${id}/vote/up/true`,
    contentType: 'application/json',
    method: 'POST',
  })

  const {
    error: undoVoteDownError,
    apiCall: undoVoteDownApiCall,
    loading: undoVoteDownLoading,
  } = useCallAPI({
    url: `${process.env.NEXT_PUBLIC_YOTPO_BASE_URL}/reviews/${id}/vote/down/true`,
    contentType: 'application/json',
    method: 'POST',
  })

  const [votesUp, setVotesUp] = useState<number>(votes_up ?? 0)
  const [hasLeftVoteUp, setHasLeftVoteUp] = useState<boolean>(false)

  const [votesDown, setVotesDown] = useState<number>(votes_down ?? 0)
  const [hasLeftVoteDown, setHasLeftVoteDown] = useState<boolean>(false)
  const [isDisplayAlertPrompt, setisDisplayAlertPrompt] = useState<boolean>(false)
  const [isDisplayErrorPrompt, setIsDisplayErrorPrompt] = useState<boolean>(false)
  const [decodedContent, setDecodedContent] = useState<any>(false)

  const handleVoteUp = async () => {
    if (!isUserLoggedIn) {
      setIsDisplayErrorPrompt(true)
      return
    }
    if (!hasLeftVoteUp && !hasLeftVoteDown) {
      const res = await voteUpApiCall()
      if (res?.status === 200) {
        setHasLeftVoteUp(true)
        setVotesUp(votesUp + 1)
        setisDisplayAlertPrompt(true)
      }
    }
    if (hasLeftVoteDown) {
      const res = await voteUpApiCall()
      if (res?.status === 200) {
        const undoRes = await undoVoteDownApiCall()
        if (undoRes?.status === 200) {
          setHasLeftVoteDown(false)
          setHasLeftVoteUp(true)
          setVotesUp(votesUp + 1)
          setVotesDown(votesDown - 1)
          setisDisplayAlertPrompt(true)
        }
      }
    }
  }
  const handleVoteDown = async () => {
    if (!isUserLoggedIn) {
      setIsDisplayErrorPrompt(true)
      return
    }
    if (!hasLeftVoteUp && !hasLeftVoteDown) {
      const res = await voteDownApiCall()
      if (res?.status === 200) {
        setHasLeftVoteDown(true)
        setVotesDown(votesDown + 1)
        setisDisplayAlertPrompt(true)
      }
    }
    if (hasLeftVoteUp) {
      const res = await voteDownApiCall()
      if (res?.status === 200) {
        const undoRes = await undoVoteUpApiCall()
        if (undoRes?.status === 200) {
          setHasLeftVoteDown(true)
          setHasLeftVoteUp(false)
          setVotesDown(votesDown + 1)
          setVotesUp(votesUp - 1)
          setisDisplayAlertPrompt(true)
        }
      }
    }
  }

  const decodeHtml = (text) => {
    const txt = document.createElement('textarea')
    txt.innerHTML = text
    return txt.value
  }

  useEffect(() => {
    setDecodedContent(decodeHtml(content))
  }, [content])

  const logoInitialsArry = user?.display_name?.split(' ')
  const firstInitial = logoInitialsArry?.at(0)?.toUpperCase().charAt(0)
  const secondInitial = logoInitialsArry?.at(1)?.toUpperCase().charAt(0)

  const logoInitials =
    firstInitial && firstInitial?.length > 0
      ? `${firstInitial ?? ''}${secondInitial ?? ''}`
      : userName?.charAt(0)?.toUpperCase()
  return (
    <div
      key={id}
      className={`mb-[20px] gap-x-10 gap-y-5 py-1 ${
        isHomePageItem
          ? 'rounded-md border-[1px] border-20-grey bg-pure-white px-5 py-4 lg:block'
          : 'lg:grid lg:grid-cols-[70fr_30fr]'
      }`}
    >
      <div className='grid grid-cols-1'>
        <div className='mb-[10px] lg:mb-0'>
          <Rating
            name='size-small'
            defaultValue={props?.score}
            size='small'
            readOnly
            precision={0.5}
            className='ml-[-8px] scale-90 align-bottom'
          />
          <div className='Type-Base-Regular mt-2 flex justify-start pb-2'>
            <div
              className={`${
                isHomePageItem ? 'Type-Large-Bold' : 'Type-Large-Medium'
              } flex items-center gap-2 pb-2 text-black`}
            >
              {user?.display_name ?? userName ?? 'Anonymous'}
              {companyName && <span>{`, ${companyName}`}</span>}
              {displayDate && <span>|</span>}
              {displayDate && (
                <span className='Type-Large-Regular pr-2 text-medium-dark-gray'>
                  {formatTimeAgo(numOfDaysPosted)}
                </span>
              )}
            </div>
          </div>

          <p className='Type-XL-Regular inline pb-2 text-black'>{decodedContent}</p>
          {/* {images_data?.length > 0 && (
          <ReviewItemImageList images_data={images_data} className='my-5 lg:hidden' />
        )} */}
          {displayVotes && (
            <p className='flex items-center pt-2'>
              <span className='Type-XL-Bold inline-block items-center pr-2 text-50-grey'>
                {i18n._('Was this review helpful?')}
              </span>
              {voteUpLoading || voteDownLoading || undoVoteUpLoading || undoVoteDownLoading ? (
                <Spinner className='' color='purple' />
              ) : (
                <Image
                  src={recommend}
                  alt='Recommend'
                  className='scale-75 cursor-pointer hover:scale-90 active:scale-100'
                  unoptimized
                  onClick={() => handleVoteUp()}
                />
              )}
              <span className='pl-[4px] pr-3 text-70-grey'>{votesUp}</span>
              {voteUpLoading || voteDownLoading || undoVoteUpLoading || undoVoteDownLoading ? (
                <Spinner className='' color='purple' />
              ) : (
                <Image
                  className='scale-75 cursor-pointer hover:scale-90 active:scale-100'
                  src={notRecommend}
                  alt='Not Recommend'
                  unoptimized
                  onClick={() => handleVoteDown()}
                />
              )}
              <span className='pl-[4px] text-70-grey'>{votesDown}</span>
            </p>
          )}
        </div>
      </div>
      {displayImages && (
        <div>
          {images_data?.length > 0 && (
            <ReviewItemImageList images_data={images_data} className='my-5 hidden lg:flex' />
          )}
        </div>
      )}
      {displayAvatar && (
        <div className='hidden w-full items-start justify-end lg:flex'>
          <Image
            src={avatar_url ?? ''}
            alt='user_brand'
            unoptimized
            className='aspect-square h-auto w-[150px] rounded-[50%]'
            width={150}
            height={150}
          />
        </div>
      )}
      {displayReplies && comment?.content && (
        <div className='ml-[8%] rounded-b-[16px] bg-lightest-gray p-[20px] text-70-grey'>
          <div className='mb-2 flex items-center justify-start gap-2 text-medium-dark-gray'>
            <span className='Type-Large-Bold flex items-center text-templi-purple'>
              {i18n._('Templi')}
            </span>
            |
            <span className='Type-Base-Regular pr-2 '>{formatTimeAgo(commentNumOfDaysPosted)}</span>
          </div>
          <span className='Type-XL-Regular'>{comment?.content}</span>
        </div>
      )}
      <AlertPrompt
        title={i18n._('Thank you')}
        isDisplayed={isDisplayAlertPrompt}
        setIsDisplayed={setisDisplayAlertPrompt}
        message={i18n._('Thank you for leaving a feedback!')}
      />
      <AlertPrompt
        title={i18n._('Oops!')}
        isDisplayed={isDisplayErrorPrompt}
        setIsDisplayed={setIsDisplayErrorPrompt}
        message={i18n._('You must be logged in to leave a feedback for a review.')}
      />
      <ErrorPrompt error={error ?? voteDownError ?? undoVoteUpApiCallError ?? undoVoteDownError} />
    </div>
  )
}
